"use client"

import { useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { Smartphone, Phone, CreditCard, History, Wallet, TrendingUp, Users, DollarSign } from "lucide-react"
import Card from "../components/Card"
import Button from "../components/Button"

const Dashboard = () => {
  const { user } = useAuth()
  const { transactions } = useWallet()
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  if (!user) return null

  const quickActions = [
    {
      title: "Buy Data",
      description: "Purchase data for any network",
      icon: <Smartphone className="h-8 w-8 text-blue-600" />,
      link: "/buy-data",
      color: "bg-blue-50 hover:bg-blue-100",
    },
    {
      title: "Buy Airtime",
      description: "Top up airtime instantly",
      icon: <Phone className="h-8 w-8 text-green-600" />,
      link: "/buy-airtime",
      color: "bg-green-50 hover:bg-green-100",
    },
    {
      title: "Airtime to Cash",
      description: "Convert airtime to cash",
      icon: <TrendingUp className="h-8 w-8 text-yellow-600" />,
      link: "/airtime-to-cash",
      color: "bg-yellow-50 hover:bg-yellow-100",
    },
    {
      title: "Pay Bills",
      description: "Cable TV, electricity & more",
      icon: <CreditCard className="h-8 w-8 text-purple-600" />,
      link: "/pay-bills",
      color: "bg-purple-50 hover:bg-purple-100",
    },
    {
      title: "Fund Wallet",
      description: "Add money to your wallet",
      icon: <Wallet className="h-8 w-8 text-red-600" />,
      link: "/wallet",
      color: "bg-red-50 hover:bg-red-100",
    },
  ]

  const stats = [
    {
      title: "Wallet Balance",
      value: `₦${user.balance?.toLocaleString() || "0"}`,
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      color: "text-green-600",
    },
    {
      title: "Total Transactions",
      value: transactions.length,
      icon: <History className="h-6 w-6 text-blue-600" />,
      color: "text-blue-600",
    },
    {
      title: "This Month",
      value: transactions.filter((t) => {
        const transactionDate = new Date(t.date)
        const currentDate = new Date()
        return (
          transactionDate.getMonth() === currentDate.getMonth() &&
          transactionDate.getFullYear() === currentDate.getFullYear()
        )
      }).length,
      icon: <TrendingUp className="h-6 w-6 text-purple-600" />,
      color: "text-purple-600",
    },
  ]

  const recentTransactions = transactions.slice(0, 5)

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user.name}!</h1>
          <p className="text-gray-600 mt-2">Manage your data, airtime, and bill payments from your dashboard</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-full">{stat.icon}</div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <Link key={index} to={action.link}>
                <Card className={`${action.color} transition-colors cursor-pointer`}>
                  <div className="text-center">
                    <div className="flex justify-center mb-4">{action.icon}</div>
                    <h3 className="text-lg font-semibold mb-2">{action.title}</h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Recent Transactions</h3>
              <Link to="/transactions">
                <Button variant="outline" size="sm">
                  View All
                </Button>
              </Link>
            </div>
            {recentTransactions.length > 0 ? (
              <div className="space-y-4">
                {recentTransactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{transaction.type}</p>
                      <p className="text-sm text-gray-600">
                        {transaction.network} - {transaction.phone}
                      </p>
                      <p className="text-xs text-gray-500">{new Date(transaction.date).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">-₦{transaction.amount?.toLocaleString()}</p>
                      <p
                        className={`text-xs ${
                          transaction.status === "completed"
                            ? "text-green-600"
                            : transaction.status === "pending"
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {transaction.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No transactions yet</p>
                <p className="text-sm text-gray-500">Your recent transactions will appear here</p>
              </div>
            )}
          </Card>

          {/* Account Summary */}
          <Card>
            <h3 className="text-xl font-semibold mb-6">Account Summary</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Wallet className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Wallet Balance</p>
                    <p className="text-sm text-gray-600">Available funds</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-green-600">₦{user.balance?.toLocaleString() || "0"}</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Account Type</p>
                    <p className="text-sm text-gray-600">Regular User</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  Upgrade
                </Button>
              </div>

              <div className="pt-4">
                <Link to="/wallet">
                  <Button className="w-full">Fund Wallet</Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
